import { useState, useEffect, useCallback } from 'react';
import { auditLogAPI } from '../lib/api';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import { AdminPageShell } from '../components/AdminShared';
import { Search, ArrowRight, Loader2 } from 'lucide-react';

export default function AdminAuditLogPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entityFilter, setEntityFilter] = useState('all');
  const [actionFilter, setActionFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (entityFilter !== 'all') params.entity_type = entityFilter;
      if (actionFilter !== 'all') params.action = actionFilter;
      if (search.trim()) params.search = search.trim();
      if (dateFrom) params.date_from = new Date(dateFrom).toISOString();
      if (dateTo) params.date_to = new Date(dateTo + 'T23:59:59').toISOString();
      const res = await auditLogAPI.list(params);
      setLogs(res.data);
    } catch { /* ignore */ }
    setLoading(false);
  }, [entityFilter, actionFilter, search, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const actionColor = (action) => {
    if (action === 'created') return 'bg-green-500/10 text-green-500 border-green-500/20';
    if (action === 'deleted') return 'bg-red-500/10 text-red-500 border-red-500/20';
    return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
  };

  const entityLabel = (t) => (t || '').replace(/_/g, ' ');

  const SKIP_KEYS = new Set(['updated_at', 'updated_by', 'id', 'created_at']);

  return (
    <AdminPageShell title="Audit Log" subtitle="Track all changes to pay rates and fee schedules" testId="admin-audit-log-page"
      actions={null} loading={false}>

      {/* Filters Bar */}
      <div className="flex flex-wrap items-end gap-3 mb-4" data-testid="audit-filters">
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">Entity Type</label>
          <Select value={entityFilter} onValueChange={setEntityFilter}>
            <SelectTrigger className="w-44 h-9" data-testid="audit-entity-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="fee_schedule">Fee Schedules</SelectItem>
              <SelectItem value="pay_schedule">Pay Schedules</SelectItem>
              <SelectItem value="pay_override">Pay Overrides</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">Action</label>
          <Select value={actionFilter} onValueChange={setActionFilter}>
            <SelectTrigger className="w-36 h-9" data-testid="audit-action-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Actions</SelectItem>
              <SelectItem value="created">Created</SelectItem>
              <SelectItem value="updated">Updated</SelectItem>
              <SelectItem value="deleted">Deleted</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">From</label>
          <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-40 h-9" data-testid="audit-date-from" />
        </div>
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">To</label>
          <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-40 h-9" data-testid="audit-date-to" />
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="text-xs text-muted-foreground mb-1 block">Search</label>
          <div className="relative">
            <Search className="absolute left-2.5 top-2 w-4 h-4 text-muted-foreground" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="User name, email, or entity ID..." className="pl-8 h-9" data-testid="audit-search" />
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="rounded-lg border bg-card">
        <ScrollArea className="h-[calc(100vh-18rem)]">
          {loading ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
          ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead className="w-40">Timestamp</TableHead>
                <TableHead className="w-40">User</TableHead>
                <TableHead className="w-28">Type</TableHead>
                <TableHead className="w-24">Action</TableHead>
                <TableHead>Changes</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {logs.map((log, i) => (
                  <TableRow key={log.id || i} data-testid={`audit-row-${i}`}>
                    <TableCell className="text-xs font-mono whitespace-nowrap text-muted-foreground">{new Date(log.timestamp).toLocaleString()}</TableCell>
                    <TableCell>
                      <p className="text-sm font-medium">{log.user_name}</p>
                      <p className="text-[10px] text-muted-foreground">{log.user_email}</p>
                    </TableCell>
                    <TableCell><Badge variant="outline" className="text-[10px] capitalize">{entityLabel(log.entity_type)}</Badge></TableCell>
                    <TableCell><Badge className={`text-[10px] ${actionColor(log.action)}`}>{log.action}</Badge></TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        {Object.entries(log.changes || {}).filter(([k]) => !SKIP_KEYS.has(k)).map(([k, newVal]) => {
                          const oldVal = log.old_values?.[k];
                          const hasOld = oldVal !== undefined && oldVal !== null;
                          return (
                            <div key={k} className="flex items-center gap-1.5 text-xs">
                              <span className="text-muted-foreground font-medium min-w-[100px]">{k.replace(/_/g, ' ')}:</span>
                              {hasOld && log.action === 'updated' ? (
                                <>
                                  <span className="text-red-400 line-through">{formatVal(oldVal)}</span>
                                  <ArrowRight className="w-3 h-3 text-muted-foreground shrink-0" />
                                  <span className="text-green-400 font-medium">{formatVal(newVal)}</span>
                                </>
                              ) : (
                                <span className="font-mono">{formatVal(newVal)}</span>
                              )}
                            </div>
                          );
                        })}
                        {log.action === 'deleted' && log.old_values && Object.entries(log.old_values).filter(([k]) => !SKIP_KEYS.has(k) && !['_id'].includes(k)).length > 0 && (
                          <details className="text-xs text-muted-foreground">
                            <summary className="cursor-pointer hover:text-foreground">Deleted record details</summary>
                            <div className="mt-1 pl-2 space-y-0.5">
                              {Object.entries(log.old_values).filter(([k]) => !SKIP_KEYS.has(k) && k !== '_id').map(([k, v]) => (
                                <div key={k}><span className="text-muted-foreground">{k}:</span> <span className="font-mono text-red-400">{formatVal(v)}</span></div>
                              ))}
                            </div>
                          </details>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {logs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-12 text-muted-foreground">
                      No audit log entries found. Changes to fee schedules, pay rates, and pay overrides will appear here.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </ScrollArea>
      </div>
      <p className="text-xs text-muted-foreground mt-2">{logs.length} entries shown</p>
    </AdminPageShell>
  );
}

function formatVal(v) {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}
